<template>
  <div class="flex flex-col gap-6 animate-rise" data-testid="data-view">
    <Header v-if="showShareHeader" />
    <section v-if="isBootstrapping" class="panel">
      <p class="label">{{ t("common.loading-section") }}</p>
      <p class="mt-3 text-sm text-slate-500">
        {{ t("common.fetching-player") }}
      </p>
    </section>

    <section v-else-if="bootstrapError" class="panel">
      <p class="label">{{ t("common.unable-to-load-section") }}</p>
      <p class="mt-3 text-sm font-semibold text-rose-600">
        {{ bootstrapError }}
      </p>
      <button
        class="btn-secondary mt-6"
        type="button"
        @click="handleBackToForm"
        data-testid="back-to-form"
      >
        {{ t("common.back-to-form") }}
      </button>
    </section>

    <template v-else>
      <section
        class="sm:rounded-3xl sm:border sm:border-white/60 sm:bg-white/70 sm:p-6 sm:shadow-[0_20px_60px_-40px_rgba(5,46,22,0.35)] sm:backdrop-blur"
      >
        <div class="flex flex-col gap-6">
          <!-- <p class="label text-base sm:text-sm">{{ t('career-view.label') }}</p> -->
          <div v-if="store.loading" class="text-sm text-slate-500">
            {{ t("career-view.fetching") }}
          </div>

          <PlayerCareerTable
            v-else
            v-model:aggregateSameClub="aggregateSameClub"
            :career-rows="store.careerRows"
          />

          <PlayerInfo
            :birth-date="store.player?.birthDate"
            :nationality="store.player?.nationality"
            :position="store.player?.position"
            :player-id="store.player?.id"
          />
        </div>
      </section>

      <PlayerGuessBox
        :player-id="store.player?.id"
        :player-name="store.player?.name"
      />

      <!-- <PlayerShareBox /> -->
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useGameStore } from "../stores/game";
import Header from "../components/Header.vue";
import PlayerCareerTable from "../components/PlayerCareerTable.vue";
import PlayerInfo from "../components/PlayerInfo.vue";
import PlayerGuessBox from "../components/PlayerGuessBox.vue";
import PlayerShareBox from "../components/PlayerShareBox.vue";
import { useText } from "../composables/useText.js";

const router = useRouter();
const route = useRoute();
const store = useGameStore();
const { t } = useText();

const isBootstrapping = ref(false);
const bootstrapError = ref("");
const showShareHeader = computed(() => route.query.share === "1");

const aggregateSameClub = computed({
  get: () => store.aggregateSameClub,
  set: (value) => store.setAggregateSameClub(value),
});

const handleBackToForm = () => {
  router.replace({ name: "form" });
};

const loadFromRoute = async () => {
  bootstrapError.value = "";
  const { playerId } = route.params;
  if (!playerId) {
    bootstrapError.value = t("common.error-missing-player");
    return;
  }

  const matchesRoute =
    store.player?.id && String(store.player.id) === String(playerId);

  if (matchesRoute) return;

  try {
    isBootstrapping.value = true;
    await store.loadGameFromPlayerId({ playerId });
  } catch (error) {
    bootstrapError.value = store.error || t("common.error-load-player");
  } finally {
    isBootstrapping.value = false;
  }
};

watch(
  () => [route.params.playerId],
  () => {
    loadFromRoute();
  },
  { immediate: true },
);
</script>
